import { useState, useEffect } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Button, Input, FormControl, FormLabel, Select, Textarea, VStack, HStack, useToast,
} from '@chakra-ui/react';
import { atualizarDevolucaoManual } from '../../services/api';

const STATUS_OPTIONS = [
    ['opened', 'Aberta'],
    ['label_generated', 'Etiqueta gerada'],
    ['shipped', 'Enviada'],
    ['delivered', 'Entregue'],
    ['closed', 'Encerrada'],
    ['not_delivered', 'Não entregue'],
    ['cancelled', 'Cancelada'],
    ['failed', 'Falhada'],
    ['expired', 'Expirada'],
];
const MONEY_OWNER_OPTIONS = [
    ['buyer', 'Devolvido ao cliente'],
    ['seller', 'Valor recuperado'],
    ['unknown', 'Indefinido'],
];
const STATUS_MONEY_OPTIONS = [
    ['', 'Não informado'],
    ['refunded', 'Reembolsado'],
    ['available', 'Disponível'],
    ['retained', 'Retido'],
];
const SHIPPING_PAID_BY_OPTIONS = [
    ['', 'Não informado'],
    ['buyer', 'Comprador'],
    ['seller', 'Vendedor'],
];

const PROBLEMAS_SUGERIDOS = [
    ['', 'Escolher motivo pronto (opcional)'],
    ['A minha compra chegou em boas condições, mas eu não a quero mais', 'Arrependimento — chegou bem mas não quer mais'],
    ['Me arrependi da compra', 'Arrependimento — simples'],
    ['Informava ser compatível com meu veículo, mas não é', 'Não é compatível com o veículo'],
    ['É incompatível com meu veículo', 'Incompatível com o veículo'],
    ['Descumpre as características do anúncio', 'Descumpre as características do anúncio'],
    ['Não é o que dizia o anúncio', 'Não é o que dizia o anúncio'],
    ['Não era da marca original', 'Não era da marca original'],
    ['Recebi outro tipo de produto', 'Recebi outro tipo de produto'],
    ['Recebi um produto diferente do que comprei', 'Recebi produto diferente do comprado'],
    ['Não é da cor, tamanho ou modelo que escolhi', 'Cor / tamanho / modelo errado'],
    ['É da cor / tamanho / modelo escolhido, porém não serviu', 'Não serviu (mesmo sendo o certo)'],
    ['A embalagem estava danificada', 'Embalagem danificada'],
    ['O produto chegou avariado', 'Produto chegou avariado'],
    ['Chegou bem', 'Chegou bem'],
];

const RESOLUCOES_SUGERIDAS = [
    ['', 'Escolher resolução pronta (opcional)'],
    ['Devolução finalizada com reembolso para o comprador', 'Devolução finalizada com reembolso para o comprador'],
    ['Item devolvido', 'Item devolvido'],
    ['Prazo de devolução expirou', 'Prazo de devolução expirou'],
    ['Devolução cancelada', 'Devolução cancelada'],
    ['Pagamento reembolsado', 'Pagamento reembolsado'],
    ['Comprador preferiu ficar com o produto', 'Comprador preferiu ficar com o produto'],
    ['Decisão por cobertura (ML)', 'Decisão por cobertura (ML)'],
    ['Decisão do armazém/logística', 'Decisão do armazém/logística'],
    ['Prazo de troca expirou', 'Prazo de troca expirou'],
    ['Reclamação aberta por engano', 'Reclamação aberta por engano'],
    ['Item trocado', 'Item trocado'],
    ['Sem cobertura de compra garantida', 'Sem cobertura de compra garantida'],
];

const EditarDevolucaoManualModal = ({ isOpen, onClose, devolucao, onUpdated }) => {
    const toast = useToast();
    const [form, setForm] = useState({});
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!devolucao) return;
        const adicional = devolucao.dados_adicionais || {};
        setForm({
            problem: adicional.problem || '',
            title: adicional.title || '',
            description: adicional.description || '',
            resolution: typeof adicional.resolution === 'string' ? adicional.resolution : '',
            status: devolucao.status || 'opened',
            money_owner: devolucao.money_owner || 'unknown',
            status_money: devolucao.status_money || '',
            refund_amount: devolucao.refund_amount ?? '',
            shipping_cost: devolucao.shipping_cost ?? '',
            shipping_paid_by: devolucao.shipping_paid_by || '',
            review_due_date: devolucao.review_due_date_util || '',
        });
    }, [devolucao]);

    const salvar = async () => {
        if (!devolucao?.id) return;
        if (!form.problem?.trim()) {
            toast({ title: 'Descreva o problema', status: 'warning', duration: 3000 });
            return;
        }
        const payload = {
            problem: form.problem,
            title: form.title || null,
            description: form.description || null,
            resolution: form.resolution || null,
            status: form.status,
            money_owner: form.money_owner,
            status_money: form.status_money || null,
            refund_amount: form.refund_amount === '' ? null : Number(form.refund_amount),
            shipping_cost: form.shipping_cost === '' ? null : Number(form.shipping_cost),
            shipping_paid_by: form.shipping_paid_by || null,
            review_due_date: form.review_due_date || null,
        };
        setSalvando(true);
        try {
            const resp = await atualizarDevolucaoManual(devolucao.id, payload);
            if (resp?.success) {
                toast({ title: 'Devolução atualizada', status: 'success', duration: 2500 });
                onUpdated && onUpdated();
                onClose && onClose();
            } else {
                toast({
                    title: 'Falha ao atualizar',
                    description: resp?.error || 'Erro desconhecido',
                    status: 'error', duration: 5000,
                });
            }
        } finally { setSalvando(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '2xl' }} scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent my={{ base: 0, md: 16 }} maxH={{ base: '100vh', md: '85vh' }}>
                <ModalHeader>Editar devolução manual</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={3} align="stretch">
                        <FormControl isRequired>
                            <FormLabel>Problema</FormLabel>
                            <Select
                                mb={2} size="sm"
                                value=""
                                onChange={(e) => { if (e.target.value) setForm({ ...form, problem: e.target.value }); }}
                            >
                                {PROBLEMAS_SUGERIDOS.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
                            </Select>
                            <Textarea value={form.problem || ''} onChange={e => setForm({ ...form, problem: e.target.value })} rows={2} />
                        </FormControl>
                        <HStack>
                            <FormControl>
                                <FormLabel>Status</FormLabel>
                                <Select value={form.status || 'opened'} onChange={e => setForm({ ...form, status: e.target.value })}>
                                    {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Destino do dinheiro</FormLabel>
                                <Select value={form.money_owner || 'unknown'} onChange={e => setForm({ ...form, money_owner: e.target.value })}>
                                    {MONEY_OWNER_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </Select>
                            </FormControl>
                        </HStack>
                        <HStack>
                            <FormControl>
                                <FormLabel>Status do dinheiro</FormLabel>
                                <Select value={form.status_money || ''} onChange={e => setForm({ ...form, status_money: e.target.value })}>
                                    {STATUS_MONEY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Valor do reembolso (R$)</FormLabel>
                                <Input type="number" step="0.01" min="0" value={form.refund_amount ?? ''} onChange={e => setForm({ ...form, refund_amount: e.target.value })} />
                            </FormControl>
                        </HStack>
                        <HStack>
                            <FormControl>
                                <FormLabel>Custo do envio (R$)</FormLabel>
                                <Input type="number" step="0.01" min="0" value={form.shipping_cost ?? ''} onChange={e => setForm({ ...form, shipping_cost: e.target.value })} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Envio pago por</FormLabel>
                                <Select value={form.shipping_paid_by || ''} onChange={e => setForm({ ...form, shipping_paid_by: e.target.value })}>
                                    {SHIPPING_PAID_BY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </Select>
                            </FormControl>
                        </HStack>
                        <FormControl>
                            <FormLabel>Prazo para revisar</FormLabel>
                            <Input type="date" value={form.review_due_date || ''} onChange={e => setForm({ ...form, review_due_date: e.target.value })} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Título</FormLabel>
                            <Input value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Descrição</FormLabel>
                            <Textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Resolução</FormLabel>
                            <Select
                                mb={2} size="sm"
                                value=""
                                onChange={(e) => { if (e.target.value) setForm({ ...form, resolution: e.target.value }); }}
                            >
                                {RESOLUCOES_SUGERIDAS.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
                            </Select>
                            <Textarea value={form.resolution || ''} onChange={e => setForm({ ...form, resolution: e.target.value })} rows={2} />
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={2} onClick={onClose}>Cancelar</Button>
                    <Button colorScheme="orange" onClick={salvar} isLoading={salvando}>Salvar</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EditarDevolucaoManualModal;
